import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const OnboardingContainer = styled(Stack)(({ theme }) => ({
  minHeight: 'calc(100dvh - 72px)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

interface OnboardingForm {
  screenName: string;
  bio: string;
}

interface FormErrors {
  screenName: string;
  bio: string;
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [form, setForm] = React.useState<OnboardingForm>({ screenName: '', bio: '' });
  const [errors, setErrors] = React.useState<FormErrors>({ screenName: '', bio: '' });
  const [submitError, setSubmitError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const validate = (): boolean => {
    const next: FormErrors = { screenName: '', bio: '' };
    if (!form.screenName || form.screenName.length < 3) {
      next.screenName = 'Screen name must be at least 3 characters.';
    } else if (!/^[a-zA-Z0-9_]+$/.test(form.screenName)) {
      next.screenName = 'Only letters, numbers, and underscores allowed.';
    }
    if (form.bio.length > 160) {
      next.bio = 'Bio must be 160 characters or fewer.';
    }
    setErrors(next);
    return !next.screenName && !next.bio;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;

    const user = auth.currentUser;
    if (!user) {
      setSubmitError('No signed-in user found. Please sign up again.');
      return;
    }

    setLoading(true);
    try {
      const isGoogle = user.providerData.some(p => p.providerId === 'google.com');
      await updateProfile(user, { displayName: form.screenName });
      await setDoc(doc(db, 'users', user.uid), {
        name: form.screenName,
        bio: form.bio,
        email: user.email,
        type: isGoogle ? 'google auth' : 'email auth',
        createdAt: serverTimestamp(),
        role: 'member',
      });
      await refreshProfile();
      navigate('/');
    } catch (error) {
      console.error('Onboarding save failed:', error);
      setSubmitError('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingContainer direction="column" sx={{ justifyContent: 'center' }}>
      <Card variant="outlined">
        <Typography component="h1" variant="h4" sx={{ fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
          Set up your profile
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Choose a screen name and tell others a bit about yourself.
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <FormControl>
            <FormLabel htmlFor="screenName">Screen name</FormLabel>
            <TextField
              id="screenName"
              name="screenName"
              required
              fullWidth
              placeholder="cool_username_99"
              value={form.screenName}
              onChange={(e) => setForm({ ...form, screenName: e.target.value })}
              error={!!errors.screenName}
              helperText={errors.screenName}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="bio">Bio</FormLabel>
            <TextField
              id="bio"
              name="bio"
              fullWidth
              multiline
              variant="standard"
              rows={4}
              placeholder="Tell us about yourself... (optional, 160 chars max)"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              error={!!errors.bio}
              helperText={errors.bio || `${form.bio.length}/160`}
              slotProps={{ htmlInput: { maxLength: 160 } }}
            />
          </FormControl>
          {submitError && (
            <Typography color="error" sx={{ textAlign: 'center' }}>
              {submitError}
            </Typography>
          )}
          <Button type="submit" fullWidth variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </Box>
      </Card>
    </OnboardingContainer>
  );
}
